/**
 * What the death record says on screen — DECISIONS.md #53.
 *
 * **Every time goes through the shared formatter** (AGENTS.md §5), and the time of death and the
 * time it was established are two lines, never one: an investigation afterwards needs them apart, and
 * so does whoever reads the banner.
 *
 * **Every refusal code gets a message.** {@link deathRefusalMessage} is exhaustive over
 * {@link DeathRefusalCode}, so a code added on the server fails the typecheck here until it has words.
 */

import type { TFunction } from 'i18next'

import { formatClinicalDateTime } from '../format/clinicalDate'
import type { DeathRefusalCode, DeathRefusalParams, PoliceNotification } from './deathApi'
import type { PatientDeathView } from './patientOverviewApi'

export type DeathTranslate = TFunction<['common', 'patient', 'death']>

/** An instant in the record's one format, or the formatter's fallback for an unreadable one. */
function instant(value: string, t: DeathTranslate): string {
  try {
    return formatClinicalDateTime(value)
  } catch {
    return t('common:value.unreadable')
  }
}

/** The answer about the police, in words. An answer the build does not know is shown as stored. */
export function policeLabel(answer: string, t: DeathTranslate): string {
  switch (answer as PoliceNotification) {
    case 'notified':
      return t('death:police.notified')
    case 'not-notified':
      return t('death:police.not-notified')
    case 'not-assessed':
      return t('death:police.not-assessed')
    default:
      return answer
  }
}

/** The physician who established the death, with the title as documented. */
function pronouncer(death: PatientDeathView, t: DeathTranslate): string {
  const name = `${death.pronouncedByGivenName ?? ''} ${death.pronouncedByFamilyName ?? ''}`.trim()

  if (name === '') {
    return t('patient:practitioner.notDocumented')
  }

  // The title is record content in the documenter's words (AGENTS.md §7) and is not translated.
  return death.pronouncedByRoleTitle === null ? name : `${name} (${death.pronouncedByRoleTitle})`
}

/**
 * The banner's lines, in the order they are read: that the patient died and when, who established it
 * and when, and what was done about the police. The circumstances follow as record content, if any.
 */
export function deathBanner(death: PatientDeathView, t: DeathTranslate): readonly string[] {
  const diedAt = instant(death.diedAt, t)

  const lines = [
    death.timeOfDeathEstimated
      ? t('death:banner.diedEstimated', { diedAt })
      : t('death:banner.died', { diedAt }),
    t('death:banner.pronounced', {
      pronouncedAt: instant(death.pronouncedAt, t),
      name: pronouncer(death, t),
    }),
    t('death:banner.police', { answer: policeLabel(death.policeNotification, t) }),
  ]

  if (death.note !== null && death.note !== '') {
    lines.push(death.note)
  }

  return lines
}

/** The label of the field a refusal names, for the sentence that says which one. */
function fieldLabel(field: string | undefined, t: DeathTranslate): string {
  return field === 'pronouncedAt' ? t('death:field.pronouncedAt') : t('death:field.diedAt')
}

/** The sentence for a refusal, in the interface language. */
export function deathRefusalMessage(
  code: DeathRefusalCode | 'UNKNOWN',
  params: DeathRefusalParams,
  t: DeathTranslate,
): string {
  switch (code) {
    case 'DEATH_TIME_MISSING':
      return t('death:refusal.timeMissing', { field: fieldLabel(params.field, t) })

    case 'DEATH_TIME_MALFORMED':
      return t('death:refusal.timeMalformed', { field: fieldLabel(params.field, t) })

    case 'DEATH_TIME_IN_FUTURE':
      return t('death:refusal.timeInFuture', { field: fieldLabel(params.field, t) })

    case 'DEATH_PRONOUNCED_BEFORE_DEATH':
      return t('death:refusal.pronouncedBeforeDeath')

    case 'DEATH_POLICE_NOTIFICATION_INVALID':
      return t('death:refusal.policeMissing')

    case 'DEATH_FIELD_TOO_LONG':
      return t('death:refusal.fieldTooLong', { max: params.max ?? 0 })

    case 'DEATH_ALREADY_RECORDED':
      return t('death:refusal.alreadyRecorded')

    case 'DEATH_NO_CHANGE':
      return t('death:refusal.noChange')

    case 'DEATH_VERSION_STALE':
      return t('death:refusal.stale')

    case 'DEATH_RETRACTED':
      return t('death:refusal.retracted')

    case 'DEATH_NOT_FOUND':
      return t('death:refusal.notFound')

    case 'PATIENT_NOT_FOUND':
    case 'PATIENT_RETRACTED':
      return t('death:refusal.patientNotFound')

    case 'ROLE_MAY_NOT_ESTABLISH_DEATH':
      return t('death:refusal.roleMayNotEstablishDeath')

    // Codes the form never provokes: reaching one is a client defect, not something to fix by hand.
    case 'DEATH_BODY_MISSING':
    case 'DEATH_SUPERSEDES_MISSING':
    case 'DEATH_SUPERSEDES_NOT_ALLOWED':
    case 'DEATH_STATUS_INVALID':
    case 'DEATH_STATUS_NOT_ALLOWED':
    case 'UNKNOWN':
      return t('death:refusal.unknown')
  }
}

/** No usable answer: the write may have happened, so the instruction is to look first. */
export function deathUnavailableMessage(t: DeathTranslate): string {
  return t('death:refusal.unavailable')
}
